import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, spacing } from '../../../constants/theme';
import {
  TabSection,
  SectionTitle,
  SectionDescription,
  UserCard,
  UserInfo,
  UserDetails,
  UserName,
  UserEmail,
  ActionButton
} from '../../../pages/admin/components/styled';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  getAllCompanies, 
  getCompanyUserCount, 
  adjustCompanyCredits,
  FirestoreCompany 
} from '../../../services/creditService';

const CreditManagementContainer = styled.div`
  display: grid;
  gap: ${spacing.lg};
`;

const CompanySelector = styled.div`
  background: ${colors.background.secondary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 12px;
  padding: ${spacing.lg};
`;

const CompanySelect = styled.select`
  width: 100%;
  padding: ${spacing.md};
  background: ${colors.background.primary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 8px;
  color: ${colors.text.primary};
  font-size: 1rem;
  margin-bottom: ${spacing.md};

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const CreditForm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
  margin-top: ${spacing.md};
`;

const CreditInput = styled.input`
  padding: ${spacing.md};
  background: ${colors.background.primary};
  border: 1px solid ${colors.background.tertiary};
  border-radius: 8px;
  color: ${colors.text.primary};
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const CreditActions = styled.div`
  display: flex;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

const NotificationMessage = styled.div<{ $type: 'success' | 'error' }>`
  background: ${props => props.$type === 'success' ? colors.success + '20' : colors.error + '20'};
  border: 1px solid ${props => props.$type === 'success' ? colors.success : colors.error};
  color: ${props => props.$type === 'success' ? colors.success : colors.error};
  padding: ${spacing.md};
  border-radius: 8px;
  margin-bottom: ${spacing.lg};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  font-weight: 500;

  &::before {
    content: '${props => props.$type === 'success' ? '✅' : '❌'}';
  }
`;

interface Company extends FirestoreCompany {
  userCount: number;
}

const CompanyCreditManagementTab: React.FC = () => {
  const { currentUser, companyData, refreshCompanyData } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Load companies from Firestore
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const firestoreCompanies = await getAllCompanies();
        
        // Enrich companies with user count
        const enrichedCompanies = await Promise.all(
          firestoreCompanies.map(async (company) => {
            const userCount = await getCompanyUserCount(company.id);
            return {
              ...company,
              userCount
            };
          })
        );

        setCompanies(enrichedCompanies);
      } catch (error) {
        console.error('Failed to load companies:', error);
        setNotification({ message: 'Failed to load companies. Please try again.', type: 'error' });
      }
    };

    loadCompanies();
  }, []);

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  const handleAddCredits = async () => {
    if (!selectedCompanyId || !creditAmount || parseInt(creditAmount) <= 0) {
      setNotification({ message: 'Please select a company and enter a valid credit amount', type: 'error' });
      return;
    }

    if (!currentUser) {
      setNotification({ message: 'User not authenticated', type: 'error' });
      return;
    }

    setIsProcessing(true);
    try {
      const amount = parseInt(creditAmount);
      const desc = description || `Super Admin added ${amount} credits`;
      
      const result = await adjustCompanyCredits(selectedCompanyId, amount, currentUser.uid, desc);
      
      if (result.success) {
        setNotification({ 
          message: `Successfully added ${amount} credits to ${selectedCompany?.name}`, 
          type: 'success' 
        });
        setCreditAmount('');
        setDescription('');
        
        // Refresh companies data for the table
        const refreshedCompanies = await getAllCompanies();
        const enrichedCompanies = await Promise.all(
          refreshedCompanies.map(async (company) => {
            const userCount = await getCompanyUserCount(company.id);
            return { ...company, userCount };
          })
        );
        setCompanies(enrichedCompanies);
        
        // If the admin modified their own company's credits, refresh AuthContext company data
        if (companyData && selectedCompanyId === companyData.id) {
          await refreshCompanyData();
        }
      } else {
        setNotification({ message: result.error || 'Failed to add credits', type: 'error' });
      }
    } catch (error) {
      console.error('Credit addition failed:', error);
      setNotification({ message: 'Failed to add credits. Please try again.', type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeductCredits = async () => {
    if (!selectedCompanyId || !creditAmount || parseInt(creditAmount) <= 0) {
      setNotification({ message: 'Please select a company and enter a valid credit amount', type: 'error' });
      return;
    }

    if (selectedCompany && selectedCompany.creditBalance < parseInt(creditAmount)) {
      setNotification({ message: 'Cannot deduct more credits than available balance', type: 'error' });
      return;
    }

    if (!currentUser) {
      setNotification({ message: 'User not authenticated', type: 'error' });
      return;
    }

    setIsProcessing(true);
    try {
      const amount = -parseInt(creditAmount); // Negative for deduction
      const desc = description || `Super Admin deducted ${parseInt(creditAmount)} credits`;
      
      const result = await adjustCompanyCredits(selectedCompanyId, amount, currentUser.uid, desc);
      
      if (result.success) {
        setNotification({ 
          message: `Successfully deducted ${parseInt(creditAmount)} credits from ${selectedCompany?.name}`, 
          type: 'success' 
        });
        setCreditAmount('');
        setDescription('');
        
        // Refresh companies data for the table
        const refreshedCompanies = await getAllCompanies();
        const enrichedCompanies = await Promise.all(
          refreshedCompanies.map(async (company) => {
            const userCount = await getCompanyUserCount(company.id);
            return { ...company, userCount };
          })
        );
        setCompanies(enrichedCompanies);
        
        // If the admin modified their own company's credits, refresh AuthContext company data
        if (companyData && selectedCompanyId === companyData.id) {
          await refreshCompanyData();
        }
      } else {
        setNotification({ message: result.error || 'Failed to deduct credits', type: 'error' });
      }
    } catch (error) {
      console.error('Credit deduction failed:', error);
      setNotification({ message: 'Failed to deduct credits. Please try again.', type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Clear notification after 5 seconds
  React.useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <TabSection>
      <SectionTitle>💎 Company Credit Management</SectionTitle>
      <SectionDescription>
        Manage credit allocations for all companies on the IntuitionAI platform. Add or deduct credits 
        as needed for billing adjustments, promotional offers, or administrative purposes.
      </SectionDescription>

      {notification && (
        <NotificationMessage $type={notification.type}>
          {notification.message}
        </NotificationMessage>
      )}

      <CreditManagementContainer>
        <CompanySelector>
          <h4 style={{ color: colors.text.primary, marginBottom: spacing.md }}>Select Company</h4>
          <CompanySelect 
            value={selectedCompanyId} 
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Choose a company...</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name} ({company.domain}) - {company.creditBalance.toLocaleString()} credits
              </option>
            ))}
          </CompanySelect>

          {selectedCompany && (
            <UserCard $isActive={true}>
              <UserInfo>
                <UserDetails>
                  <UserName>{selectedCompany.name}</UserName>
                  <UserEmail>{selectedCompany.domain}</UserEmail>
                  <div style={{ 
                    fontSize: '0.9rem', 
                    color: colors.text.secondary, 
                    marginTop: spacing.sm 
                  }}>
                    <div>👥 {selectedCompany.userCount} users</div>
                    <div>💎 {selectedCompany.creditBalance.toLocaleString()} current credits</div>
                  </div>
                </UserDetails>
              </UserInfo>
            </UserCard>
          )}
        </CompanySelector>

        {selectedCompanyId && (
          <CompanySelector>
            <h4 style={{ color: colors.text.primary, marginBottom: spacing.md }}>Credit Operations</h4>
            
            <CreditForm>
              <CreditInput
                type="number"
                min="1"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="Enter credit amount"
              />
              <CreditInput
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Description (optional)"
              />
            </CreditForm>

            <CreditActions style={{ marginTop: spacing.lg }}>
              <ActionButton 
                variant="primary"
                onClick={handleAddCredits}
                disabled={isProcessing || !creditAmount}
              >
                {isProcessing ? 'Processing...' : `Add ${creditAmount || '0'} Credits`}
              </ActionButton>
              <ActionButton 
                variant="secondary"
                onClick={handleDeductCredits}
                disabled={isProcessing || !creditAmount}
              >
                {isProcessing ? 'Processing...' : `Deduct ${creditAmount || '0'} Credits`}
              </ActionButton>
            </CreditActions>
          </CompanySelector>
        )}

        <CompanySelector>
          <h4 style={{ color: colors.text.primary, marginBottom: spacing.md }}>All Companies Overview</h4>
          <div style={{ display: 'grid', gap: spacing.md }}>
            {companies.map(company => (
              <UserCard key={company.id} $isActive={true}>
                <UserInfo>
                  <UserDetails>
                    <UserName>{company.name}</UserName>
                    <UserEmail>{company.domain}</UserEmail>
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: colors.text.secondary, 
                      marginTop: spacing.sm,
                      display: 'flex',
                      gap: spacing.md
                    }}>
                      <span>👥 {company.userCount} users</span>
                      <span>💎 {company.creditBalance.toLocaleString()} credits</span>
                    </div>
                  </UserDetails>
                  <ActionButton 
                    variant="secondary"
                    onClick={() => setSelectedCompanyId(company.id)}
                  >
                    Manage
                  </ActionButton>
                </UserInfo>
              </UserCard>
            ))}
          </div>
        </CompanySelector>
      </CreditManagementContainer>
    </TabSection>
  );
};

export default CompanyCreditManagementTab;
